/**
 * Shared helpers for compliance-center form validation.
 *
 * Pattern
 * ───────
 * 1. Each dialog keeps its own  errors: Record<string,string>  state.
 * 2. Call validate() on submit → if non-empty, setErrors + toast → return.
 * 3. Call clearErr(field) inside every onChange handler.
 * 4. Wrap inputs with fieldCls() for red border; render fErr() below each input.
 * 5. In onError: if the server returns { field, message } or { [field]: message },
 *    call setServerErrors() to map them back onto the local errors state.
 */

import React from "react";

// ── Inline error renderer ──────────────────────────────────────────────────────
export function fErr(errors: Record<string, string>, field: string) {
  if (!errors[field]) return null;
  return (
    <p className="text-xs text-red-500 mt-1 flex items-center gap-1" role="alert">
      ⚠ {errors[field]}
    </p>
  );
}

// ── Input className helper ─────────────────────────────────────────────────────
export function fieldCls(
  base: string,
  errors: Record<string, string>,
  field: string
): string {
  return errors[field] ? `${base} border-red-500` : base;
}

// ── Clear one field error on change ───────────────────────────────────────────
export function clearErr(
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  field: string
) {
  setErrors((e) => {
    if (!e[field]) return e;
    const n = { ...e };
    delete n[field];
    return n;
  });
}

// ── Map server error response → field errors ──────────────────────────────────
/**
 * Servers return either:
 *   { field: "applicationName", message: "Already exists" }
 *   { applicationName: "Already exists" }
 *   { errors: { applicationName: "..." } }
 */
export function extractServerErrors(
  e: unknown
): Record<string, string> | null {
  if (!e || typeof e !== "object") return null;
  const obj = e as Record<string, unknown>;

  // { field, message }
  if (typeof obj.field === "string" && typeof obj.message === "string") {
    return { [obj.field]: obj.message };
  }

  // { errors: { ... } }
  if (obj.errors && typeof obj.errors === "object") {
    return obj.errors as Record<string, string>;
  }

  // flat { fieldName: "message" } — detect by absence of known wrapper keys
  // "error" is reserved for server error codes (e.g. AUDIT_CONTROL_NOT_FOUND on 404) and must not trigger field highlighting.
  const skip = new Set(["message", "status", "title", "type", "traceId", "error"]);
  const flat: Record<string, string> = {};
  let found = false;
  for (const [k, v] of Object.entries(obj)) {
    if (!skip.has(k) && typeof v === "string") {
      flat[k] = v;
      found = true;
    }
  }
  return found ? flat : null;
}

// ── Required-field check shorthand ────────────────────────────────────────────
export function required(
  errs: Record<string, string>,
  field: string,
  value: string | undefined | null,
  label: string
) {
  if (!value?.trim()) errs[field] = `${label} is required.`;
}
